<template>
  <div :class="navClass">
    <nav class="flex items-center justify-between p-4 md:p-8" id="navbar-default">
      <!-- Logo à gauche -->
      <div class="flex-shrink-0">
        <Link :href="projectsListUrl" class="flex items-center space-x-3 rtl:space-x-reverse">
          <ApplicationLogo/>
        </Link>
      </div>

      <!-- Zone centrale flexible (MeilisearchInput sur desktop) -->
      <div class="hidden md:flex flex-grow justify-center mx-4">
        <MeilisearchInput v-if="user" class="w-full max-w-2xl"/>
      </div>

      <!-- Zone de droite (boutons de connexion ou menu utilisateur) -->
      <div class="flex items-center">
        <template v-if="user">
          <!-- Contenu pour utilisateur connecté -->
          <Link
              class="hidden md:flex font-medium text-sm text-black hover:text-green-200 items-center whitespace-pre mr-4"
              :href="projectsListUrl"
          >
            Mes dossiers
          </Link>
          <Link
              v-if="isDofEnabled || isDbeEnabled"
              class="hidden md:flex font-medium text-sm text-black hover:text-green-200 items-center whitespace-pre mr-4"
              :href="route('qof.tracking')"
              method="get"
          >
            Suivi Signatures
          </Link>
          <DropdownNotification align="right" class="mr-3"/>
          <DropdownProfile
              align="right"
              @click="$emit('toggleNavigationDropdown')"
          />
        </template>
        <template v-else>
          <!-- Boutons pour utilisateur non connecté -->
          <div class="flex items-center flex-col sm:flex-row">
            <Link
                class="font-medium text-sm text-green-100 hover:text-green-200 flex items-center cursor-pointer mb-2 sm:mb-0 sm:mr-5 w-full sm:w-auto justify-center"
                :href="route('register')"
            >
              S'inscrire
            </Link>
            <Link
                class="transition-colors rounded-full px-4 py-2 text-sm font-medium bg-green-100 hover:bg-green-200 text-white w-full sm:w-auto flex items-center justify-center"
                :href="route('login')"
            >
              Connexion
            </Link>
          </div>
        </template>
      </div>
    </nav>
    <!-- Menu mobile -->

    <div class="w-full mb-2 md:hidden p-5">
      <MeilisearchInput v-if="user" class="w-full"/>
    </div>

    <div
        v-if="showingNavigationDropdown"
        class="md:hidden"
    >
      <div class="pt-2 pb-3 space-y-1">
        <ResponsiveNavLink v-if="user" :href="projectsListUrl" :active="route().current('projects.lists')">
          Mes dossiers
        </ResponsiveNavLink>
        <ResponsiveNavLink v-if="user && (isDofEnabled || isDbeEnabled)" :href="route('qof.tracking')"
                           :active="route().current('qof.tracking')">
          Suivi Signatures
        </ResponsiveNavLink>
      </div>

      <!-- Options de profil pour mobile -->
      <div class="pt-4 pb-1 border-t border-gray-200">
        <div v-if="user" class="flex flex-col px-4">
          <div class="font-medium text-base text-gray-800">{{ user.name }}</div>
          <div class="font-medium text-sm text-gray-500">{{ user.email }}</div>
          <div v-if="currentTeam" class="font-medium text-sm text-gray-700 mt-1">{{ currentTeam.name }}</div>
        </div>

        <div class="mt-3 space-y-1">
          <ResponsiveNavLink :href="route('profile.show')" :active="route().current('profile.show')">
            Mon Profil
          </ResponsiveNavLink>
          <ResponsiveNavLink :href="route('teams.show', currentTeam)" :active="route().current('teams.show')">
            Mon compte VigIActe
          </ResponsiveNavLink>
          <ResponsiveNavLink :href="route('billing.show')" :active="route().current('billing.show')">
            Facturation
          </ResponsiveNavLink>
          <ResponsiveNavLink :href="route('statistics.show')" :active="route().current('statistics.show')">
            Statistiques
          </ResponsiveNavLink>
          <ResponsiveNavLink v-if="$page.props.canManageWebhooks" :href="route('webhook-endpoints.index')"
                             :active="route().current('webhook-endpoints.index')">
            API & Webhooks
          </ResponsiveNavLink>
        </div>

        <!-- Team Switcher Mobile -->
        <template v-if="hasMultipleTeams">
          <div class="border-t border-gray-200 mt-3"/>
          <div class="block px-4 py-2 text-xs text-gray-800">
            Changer d'étude
          </div>
          <template v-for="teamItem in allTeams" :key="teamItem.id">
            <button
                type="button"
                class="block w-full ps-3 pe-4 py-2 border-l-4 text-start text-base font-medium transition duration-150 ease-in-out"
                :class="teamItem.id === currentTeamId
                  ? 'border-green-400 text-green-700 bg-green-50'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50 hover:border-gray-300'"
                @click="switchToTeam(teamItem)"
            >
              <div class="flex items-center">
                <svg v-if="teamItem.id === currentTeamId"
                     class="me-2 h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                     viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round"
                        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>{{ teamItem.name }}</span>
              </div>
            </button>
          </template>
        </template>

        <!-- Déconnexion -->
        <div class="border-t border-gray-200 mt-3">
          <div class="mt-3 space-y-1">
            <ResponsiveNavLink :href="route('logout')" method="post" as="button">
              Déconnexion
            </ResponsiveNavLink>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script setup>
import ApplicationLogo from '@/Components/ApplicationLogo.vue';
import DropdownNotification from '@/Components/Client/Dropdowns/DropdownNotification.vue';
import DropdownProfile from '@/Components/Client/Dropdowns/DropdownProfile.vue';
import {Link, router, usePage} from '@inertiajs/vue3';
import {computed} from 'vue';
import ResponsiveNavLink from "@/Components/ResponsiveNavLink.vue";
import MeilisearchInput from "@/Components/Client/Inputs/MeilisearchInput.vue";
import {useFeatures} from "@/Composables/useFeatures.js";

const {isDofEnabled, isDbeEnabled} = useFeatures();

const user = computed(() => usePage().props?.auth?.user)
const props = defineProps({
  showingNavigationDropdown: Boolean,
  navClass: {
    type: String,
    default: 'max-w-7xl mx-auto',
  },
});

const emit = defineEmits(['toggleNavigationDropdown']);

const projectsListUrl = computed(() => {
  try {
    const saved = JSON.parse(localStorage.getItem('vigiacte_folders_filters') || '{}');
    const params = new URLSearchParams();
    if (saved.folderStatus) params.set('folderStatus', saved.folderStatus);
    if (saved.folderType) params.set('folderType', saved.folderType);
    if (saved.orderBy) params.set('orderBy', saved.orderBy);
    const qs = params.toString();
    return qs ? `${route('projects.lists')}?${qs}` : route('projects.lists');
  } catch {
    return route('projects.lists');
  }
});

const switchToTeam = (team) => {
  router.put(route('current-team.update'), {
    team_id: team.id,
  }, {
    preserveState: false,
  });
};

const allTeams = computed(() => usePage().props?.auth?.user?.all_teams || []);
const currentTeam = computed(() => usePage().props?.auth?.user?.current_team);
const currentTeamId = computed(() => usePage().props?.auth?.user?.current_team_id);
const hasMultipleTeams = computed(() => allTeams.value.length > 1);

</script>